import React, { useState, useEffect } from 'react';
import axios from '../utils/axios';
import { useAuth } from '../context/AuthContext';
import CustomerProfileModal from './CustomerProfileModal';
import TourPackageManagement from './TourPackageManagement';
import ItineraryManagement from './ItineraryManagement';
import HotelManagement from './HotelManagement';
import TemplateSettings from './TemplateSettings';
import FollowUpManager from './FollowUpManager';
import NewLeadNotificationPopup from './NewLeadNotificationPopup';
import TourQuotationPDF from './TourQuotationPDF';
import QuotationForm from './QuotationForm';
import { toast } from './Toast';

const ReservationDashboard = () => {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showVendorStatusModal, setShowVendorStatusModal] = useState(false);
  const [vendorStatusData, setVendorStatusData] = useState({
    status: 'pending',
    notes: ''
  });
  const [showCustomerProfile, setShowCustomerProfile] = useState(false);
  const [selectedLeadForProfile, setSelectedLeadForProfile] = useState(null);
  const [showPackageManagement, setShowPackageManagement] = useState(false);
  const [showHotelManagement, setShowHotelManagement] = useState(false);
  const [activeTab, setActiveTab] = useState('leads'); // 'leads' or 'followUps'
  const [searchTerm, setSearchTerm] = useState('');
  const [showTemplateSettings, setShowTemplateSettings] = useState(false);
  const [quotationFilter, setQuotationFilter] = useState('all'); // 'all', 'sent', 'not_sent'

  // Quotation states
  const [showQuotationForm, setShowQuotationForm] = useState(false);
  const [selectedLeadForQuotation, setSelectedLeadForQuotation] = useState(null);
  const [showQuotationPDF, setShowQuotationPDF] = useState(false);
  const [quotationData, setQuotationData] = useState(null);
  const [quotations, setQuotations] = useState([]);
  const [loadingQuotations, setLoadingQuotations] = useState(false);
  const [quotationSearchTerm, setQuotationSearchTerm] = useState('');
  const [loadingRevisions, setLoadingRevisions] = useState(false);
  const [revisionRequests, setRevisionRequests] = useState([]);
  const [openDropdownId, setOpenDropdownId] = useState(null); // For 3-dot menu
  const [selectedAmendment, setSelectedAmendment] = useState(null); // For viewing amendment details
  const [showAmendmentForm, setShowAmendmentForm] = useState(false);
  const [amendmentFormData, setAmendmentFormData] = useState({ quotation: null, message: '', urgency: 'normal', type: 'change_request' });

  // Vendor Email Modal states
  const [showVendorEmailModal, setShowVendorEmailModal] = useState(false);
  const [vendorEmailData, setVendorEmailData] = useState({
    vendorEmail: '',
    subject: '',
    body: '',
    leadId: null
  });
  const [sendingVendorEmail, setSendingVendorEmail] = useState(false);

  // Vendor Contacts states
  const [vendorContacts, setVendorContacts] = useState([]);
  const [showManageVendors, setShowManageVendors] = useState(false);
  const [newVendor, setNewVendor] = useState({ name: '', email: '', phone: '', category: 'Hotel' });
  const [savingVendor, setSavingVendor] = useState(false);
  const [vendorEmailDropdownOpen, setVendorEmailDropdownOpen] = useState(false);

  // Gmail Settings Modal states
  const [showGmailSettings, setShowGmailSettings] = useState(false);
  const [gmailSettings, setGmailSettings] = useState({ emailUser: '', emailPass: '' });
  const [gmailStatus, setGmailStatus] = useState({ configured: false, email: '' });
  const [savingGmail, setSavingGmail] = useState(false);

  // Load Gmail settings on mount
  useEffect(() => {
    const loadGmailSettings = async () => {
      try {
        const token = localStorage.getItem('token') || localStorage.getItem('companyToken') || localStorage.getItem('employeeToken');
        const res = await axios.get('/email/settings', { headers: { Authorization: `Bearer ${token}` } });
        if (res.data.success) {
          setGmailStatus({ configured: res.data.emailPassSet, email: res.data.emailUser });
          setGmailSettings(prev => ({ ...prev, emailUser: res.data.emailUser || '' }));
        }
      } catch (err) { console.log('Gmail settings not loaded'); }
    };
    loadGmailSettings();
  }, []);

  const handleSaveGmailSettings = async () => {
    if (!gmailSettings.emailUser || !gmailSettings.emailPass) {
      toast.error('Please enter Gmail email and App Password');
      return;
    }
    setSavingGmail(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('companyToken') || localStorage.getItem('employeeToken');
      const res = await axios.post('/email/settings', gmailSettings, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message);
      setGmailStatus({ configured: true, email: gmailSettings.emailUser });
      setGmailSettings(prev => ({ ...prev, emailPass: '' }));
      setShowGmailSettings(false);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save Gmail settings');
    } finally {
      setSavingGmail(false);
    }
  };

  // Load vendor contacts from API
  const loadVendorContacts = async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('companyToken') || localStorage.getItem('employeeToken');
      const res = await axios.get('/vendor-contacts', { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.success) setVendorContacts(res.data.contacts || []);
    } catch (err) { console.log('Vendor contacts not loaded'); }
  };

  const handleAddVendor = async () => {
    if (!newVendor.name.trim() || !newVendor.email.trim()) {
      toast.error('Name aur Email dono zaroori hain');
      return;
    }
    setSavingVendor(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('companyToken') || localStorage.getItem('employeeToken');
      const res = await axios.post('/vendor-contacts', newVendor, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.success) {
        toast.success(`✅ ${res.data.message}`);
        setNewVendor({ name: '', email: '', phone: '', category: 'Hotel' });
        loadVendorContacts();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Vendor add nahi hua');
    } finally {
      setSavingVendor(false);
    }
  };

  const handleDeleteVendor = async (id, name) => {
    if (!window.confirm(`"${name}" ko remove karna chahte ho?`)) return;
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('companyToken') || localStorage.getItem('employeeToken');
      const res = await axios.delete(`/vendor-contacts/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.success) {
        toast.success(`🗑️ ${res.data.message}`);
        loadVendorContacts();
      }
    } catch (err) {
      toast.error('Delete nahi hua');
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setOpenDropdownId(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    fetchLeads();
    loadVendorContacts();
    if (activeTab === 'quotations') {
      fetchQuotations();
    }
  }, [activeTab]);

  const fetchLeads = async () => {
    try {
      const token = localStorage.getItem('token') ||
        localStorage.getItem('companyToken') ||
        localStorage.getItem('employeeToken');

      const response = await axios.get('/leads', {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Handle different response formats
      let allLeads = [];
      if (Array.isArray(response.data)) {
        allLeads = response.data;
      } else if (response.data.leads && Array.isArray(response.data.leads)) {
        allLeads = response.data.leads;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        allLeads = response.data.data;
      }

      console.log('📊 Total leads fetched:', allLeads.length);

      // Get current employee ID for per-employee filtering
      const employeeData = JSON.parse(localStorage.getItem('employeeData') || localStorage.getItem('employeeInfo') || '{}');
      const currentEmployeeId = employeeData.id || employeeData._id || user?.id || user?._id;
      const isAdmin = user?.userType === 'superadmin' || user?.role === 'Admin' || employeeData?.userType === 'superadmin';

      console.log('👤 Current Reservation Employee ID:', currentEmployeeId);
      console.log('👑 Is Admin:', isAdmin);

      // Debug: Check first few leads
      if (allLeads.length > 0) {
        console.log('🔍 First lead sample:', {
          customerName: allLeads[0].customerName,
          currentOwner: allLeads[0].currentOwner,
          workflowStatus: allLeads[0].workflowStatus,
          assignedToReservation: allLeads[0].assignedToReservation,
          hasQuotations: allLeads[0].quotations?.length || 0
        });
      }

      // Filter leads for reservation team
      // Show leads that are currently with reservation OR were handled by reservation (forwarded to sales)
      const reservationLeads = allLeads.filter(lead => {
        const isReservationOwner = lead.currentOwner === 'reservation';
        const isAssignedToReservation = !!lead.assignedToReservation;
        const isForwardedToSales = lead.workflowStatus === 'forwarded_to_sales';
        const isQuotationPrepared = lead.workflowStatus === 'quotation_prepared';
        const isForwardedToReservation = lead.workflowStatus === 'forwarded_to_reservation';
        const isReservationWorking = lead.workflowStatus === 'reservation_working';
        const isVendorApprovalPending = lead.workflowStatus === 'vendor_approval_pending';
        const isVendorApproved = lead.workflowStatus === 'vendor_approved';
        const hasQuotations = lead.quotations && lead.quotations.length > 0;

        // First check if lead belongs to reservation team
        const isReservationLead = isReservationOwner ||
          isAssignedToReservation ||
          isForwardedToSales ||
          isQuotationPrepared ||
          isForwardedToReservation ||
          isReservationWorking ||
          isVendorApprovalPending ||
          isVendorApproved ||
          hasQuotations;

        if (!isReservationLead) return false;

        // STRICT DATA ISOLATION: Admin sees all, employees see only their assigned leads
        if (isAdmin) return true;

        // Check if lead is assigned to current reservation employee
        const isAssignedToMe =
          lead.assignedToReservation === currentEmployeeId ||
          lead.assignedToReservation?._id === currentEmployeeId ||
          lead.assignedToReservation?.toString() === currentEmployeeId?.toString();

        // Check if any quotation was prepared by current employee
        const hasMyQuotation = lead.quotations?.some(q =>
          q.preparedBy === currentEmployeeId ||
          q.preparedBy?._id === currentEmployeeId ||
          q.preparedBy?.toString() === currentEmployeeId?.toString()
        );

        // If lead is not specifically assigned to anyone in reservation, 
        // show it to all reservation employees (unassigned leads)
        const isUnassigned = !lead.assignedToReservation;

        const shouldShow = isAssignedToMe || hasMyQuotation || isUnassigned;

        if (!shouldShow) {
          console.log('🔒 Lead filtered out (assigned to different reservation employee):', lead.customerName);
        }

        return shouldShow;
      });

      console.log('✅ Reservation leads for current employee:', reservationLeads.length);
      if (reservationLeads.length > 0) {
        console.log('🔍 Sample lead:', {
          customerName: reservationLeads[0].customerName,
          currentOwner: reservationLeads[0].currentOwner,
          workflowStatus: reservationLeads[0].workflowStatus,
          quotationsCount: reservationLeads[0].quotations?.length || 0
        });
      }

      setLeads(reservationLeads);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching leads:', error);
      setLoading(false);
    }
  };

  const fetchQuotations = async () => {
    try {
      setLoadingQuotations(true);
      const token = localStorage.getItem('token') ||
        localStorage.getItem('companyToken') ||
        localStorage.getItem('employeeToken');

      const response = await axios.get('/leads/quotations/my-quotations', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        console.log('📊 Quotations received:', response.data.quotations.length);
        console.log('🔍 First quotation sample:', response.data.quotations[0]);
        console.log('📝 lastEdited field:', response.data.quotations[0]?.lastEdited);
        setQuotations(response.data.quotations || []);
      }
      setLoadingQuotations(false);
    } catch (error) {
      console.error('Error fetching quotations:', error);
      setLoadingQuotations(false);
    }
  };

  const handleDeleteQuotation = async (quotation) => {
    if (!window.confirm(`Are you sure you want to delete this quotation for ${quotation.customerName}?\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token') ||
        localStorage.getItem('companyToken') ||
        localStorage.getItem('employeeToken');

      await axios.delete(`/leads/${quotation.leadId}/quotations/${quotation._id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Quotation deleted successfully');
      fetchQuotations();
      fetchLeads(); // Refresh leads to update quotation status
    } catch (error) {
      console.error('Error deleting quotation:', error);
      toast.error('Error deleting quotation: ' + (error.response?.data?.message || error.message));
    }
  };



  // Filter leads based on search
  const filteredLeads = leads.filter(lead => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      lead.customerName?.toLowerCase().includes(search) ||
      lead.email?.toLowerCase().includes(search) ||
      lead.contactNumber?.includes(search) ||
      lead.destination?.toLowerCase().includes(search)
    );
  });

  // Filter quotations based on search and status
  const filteredQuotations = quotations.filter(quotation => {
    // Apply search filter
    if (quotationSearchTerm) {
      const search = quotationSearchTerm.toLowerCase();
      const matchesSearch = (
        quotation.quotationNumber?.toLowerCase().includes(search) ||
        quotation.customerName?.toLowerCase().includes(search) ||
        quotation.customerEmail?.toLowerCase().includes(search) ||
        quotation.destination?.toLowerCase().includes(search)
      );
      if (!matchesSearch) return false;
    }

    // Apply status filter
    if (quotationFilter === 'sent') {
      return quotation.status === 'sent' || quotation.sentToSales === true;
    } else if (quotationFilter === 'not_sent') {
      return quotation.status === 'draft' || !quotation.sentToSales;
    } else if (quotationFilter === 'amendment') {
      return quotation.amendmentRequests?.length > 0 || quotation.status === 'amendment_requested';
    }

    return true; // 'all' filter
  });







  const updateVendorStatus = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token') ||
        localStorage.getItem('employeeToken') ||
        localStorage.getItem('companyToken');

      const workflowStatusMap = {
        'approved': 'vendor_approved',
        'rejected': 'vendor_rejected',
        'pending': 'vendor_approval_pending'
      };

      const response = await axios.post(
        `/leads/${selectedLead._id}/update-vendor-status`,
        {
          workflowStatus: workflowStatusMap[vendorStatusData.status],
          notes: vendorStatusData.notes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setShowVendorStatusModal(false);
        setVendorStatusData({ status: 'pending', notes: '' });
        fetchLeads();

        const statusMessages = {
          'approved': '✅ Vendor approved! Ready to proceed.',
          'rejected': '❌ Vendor rejected. Lead marked accordingly.',
          'pending': '⏳ Vendor approval pending. Waiting for response.'
        };

        toast.success(statusMessages[vendorStatusData.status]);
      }
    } catch (error) {
      console.error('Error updating vendor status:', error);
      toast.error('Error updating vendor status: ' + (error.response?.data?.message || error.message));
    }
  };

  const markVendorApproved = async (leadId, approvalId) => {
    try {
      const token = localStorage.getItem('token') ||
        localStorage.getItem('employeeToken') ||
        localStorage.getItem('companyToken');
      await axios.post(`/leads/${leadId}/approve-vendor`,
        { approvalId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchLeads();
      toast.success('Vendor approved successfully!');
    } catch (error) {
      console.error('Error approving vendor:', error);
      toast.error('Error approving vendor: ' + (error.response?.data?.message || error.message));
    }
  };

  // Helper: Build vendor email data from lead
  const buildVendorEmailFromLead = (lead) => {
    const emailSubject = `Vendor Approval Request - ${lead.tourCode || lead.customerName} - ${lead.destination}`;
    const emailBody = `Dear Vendor,

We have a new booking request that requires your approval and quotation.

${lead.tourCode ? `🎫 TOUR CODE: ${lead.tourCode}\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n` : ''}CUSTOMER INFORMATION:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
• Customer Name: ${lead.customerName}
• Email: ${lead.email || 'N/A'}
• Contact: ${lead.contactNumber || 'N/A'}

TRAVEL DETAILS:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
• Destination: ${lead.destination}
• Tour Type: ${lead.tourType || 'N/A'}
• Travel Date: ${new Date(lead.travelDate).toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
• Number of Nights: ${lead.numberOfNights || 'N/A'}
• Number of Pax: ${lead.numberOfPax || 'N/A'} Adults
• Children: ${lead.numberOfChildren || 0}

ACCOMMODATION REQUIREMENTS:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
• Number of Rooms: ${lead.numberOfRooms || 'N/A'}
• Hotel Category: ${lead.hotelCategory || 'N/A'}
• Meal Plan: ${lead.mealPlan || 'N/A'}
${lead.extraBed && lead.extraBed !== 'None' ? `• Extra Bed: ${lead.extraBed}` : ''}

TRANSPORT:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
• Vehicle Type: ${lead.vehicleType || 'N/A'}
${lead.pickUp ? `• Pick-Up: ${lead.pickUp}` : ''}
${lead.drop ? `• Drop: ${lead.drop}` : ''}

${lead.specialNote ? `SPECIAL NOTES:\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n${lead.specialNote}\n` : ''}ACTION REQUIRED:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Please review the above requirements and provide your quotation at the earliest.

Best regards,
Tours & Travels Team`;
    return { subject: emailSubject, body: emailBody, leadId: lead._id };
  };

  // Open vendor email modal for a lead (from leads table)
  const openVendorEmailForLead = (lead) => {
    const { subject, body, leadId } = buildVendorEmailFromLead(lead);
    setVendorEmailData({ vendorEmail: '', subject, body, leadId });
    setShowVendorEmailModal(true);
  };

  // Open vendor email modal for a quotation (from quotations table)
  const handleResendToVendor = async (quotation) => {
    try {
      const token = localStorage.getItem('token') ||
        localStorage.getItem('companyToken') ||
        localStorage.getItem('employeeToken');

      const response = await axios.get(
        `/leads/${quotation.leadId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const lead = response.data;

      const hasAmendments = quotation.amendmentRequests && quotation.amendmentRequests.length > 0;
      let emailSubject, emailBody;

      if (hasAmendments) {
        const latestAmendment = quotation.amendmentRequests[quotation.amendmentRequests.length - 1];
        emailSubject = `⚠️ AMENDMENT REQUEST - ${lead?.tourCode || quotation.tourCode || lead?.customerName || quotation.customerName} - ${lead?.destination || quotation.destination}`;
        emailBody = `Dear Vendor,

⚠️ AMENDMENT REQUEST

Please note the following amendment:

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
AMENDMENT DETAILS:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

${latestAmendment.amendmentDetails || latestAmendment.changes || 'No details provided'}

${latestAmendment.notes ? `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
ADDITIONAL NOTES:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

${latestAmendment.notes}

` : ''}━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Please provide your revised quotation at the earliest.

Best regards,
Tours & Travels Team`;
      } else {
        const data = buildVendorEmailFromLead(lead || {});
        emailSubject = data.subject || `Vendor Approval Request - ${quotation.tourCode || quotation.customerName} - ${quotation.destination}`;
        emailBody = data.body;
      }

      setVendorEmailData({
        vendorEmail: '',
        subject: emailSubject,
        body: emailBody,
        leadId: quotation.leadId
      });
      setShowVendorEmailModal(true);
    } catch (error) {
      console.error('Error in handleResendToVendor:', error);
      // Fallback: open modal with basic data
      setVendorEmailData({
        vendorEmail: '',
        subject: `Vendor Request - ${quotation.tourCode || quotation.customerName} - ${quotation.destination}`,
        body: `Dear Vendor,\n\nPlease provide quotation for:\n• Customer: ${quotation.customerName}\n• Destination: ${quotation.destination}\n\nBest regards,\nTours & Travels Team`,
        leadId: quotation.leadId
      });
      setShowVendorEmailModal(true);
    }
  };

  // Send vendor email via backend API
  const handleSendVendorEmail = async () => {
    if (!vendorEmailData.vendorEmail) {
      toast.error('Please enter vendor email address');
      return;
    }
    setSendingVendorEmail(true);
    try {
      const token = localStorage.getItem('token') ||
        localStorage.getItem('companyToken') ||
        localStorage.getItem('employeeToken');

      const response = await axios.post('/email/send-vendor-email', {
        vendorEmail: vendorEmailData.vendorEmail,
        subject: vendorEmailData.subject,
        body: vendorEmailData.body,
        leadId: vendorEmailData.leadId
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast.success(`✅ ${response.data.message}`);
      setShowVendorEmailModal(false);
      fetchLeads();
      fetchQuotations();
    } catch (error) {
      console.error('Error sending vendor email:', error);
      const errorCode = error.response?.data?.code;
      if (errorCode === 'EMAIL_NOT_CONFIGURED') {
        toast.error('Email service not configured. Use Copy or WhatsApp instead.');
      } else {
        toast.error('Email failed: ' + (error.response?.data?.message || error.message));
      }
    } finally {
      setSendingVendorEmail(false);
    }
  };



  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Loading dashboard...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* New Lead Notification Popup */}
      <NewLeadNotificationPopup
        notificationType="lead_forwarded"
        onNewLeadDetected={(leadIds) => {
          console.log('🔔 New leads detected, refreshing...', leadIds);
          fetchLeads();
        }}
        onViewLead={(lead) => {
          setSelectedLead(lead);
        }}
      />

      {/* Amendment Request Notification Popup */}
      <NewLeadNotificationPopup
        notificationType="amendment_requested"
        onNewLeadDetected={(amendmentIds) => {
          console.log('🔔 New amendment requests detected, refreshing...', amendmentIds);
          fetchLeads();
          if (activeTab !== 'revisions') {
            setActiveTab('revisions');
          }
        }}
        onViewLead={(lead) => {
          setSelectedLead(lead);
          setActiveTab('revisions');
        }}
      />

      {/* Sidebar */}
      <div className="w-56 bg-gradient-to-b from-indigo-900 to-indigo-800 text-white shadow-2xl flex flex-col fixed h-screen z-50 left-0 top-0">
        {/* Header */}
        <div className="p-4 border-b border-indigo-700">
          <h2 className="text-lg font-bold truncate">🏢 Tours & Travels</h2>
          <p className="text-xs text-indigo-200 mt-0.5 truncate">Reservation Team</p>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1.5 overflow-y-auto">
          <button
            onClick={() => setActiveTab('leads')}
            className={`w-full text-left px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-2 ${activeTab === 'leads'
              ? 'bg-white text-indigo-900 shadow-lg font-semibold'
              : 'text-indigo-100 hover:bg-indigo-700 hover:text-white'
              }`}
          >
            <span className="text-xs">📋</span>
            <div className="flex-1">
              <div className="text-sm font-medium">Leads</div>
              <div className="text-xs opacity-75">{leads.length} active</div>
            </div>
          </button>

          <button
            onClick={() => setActiveTab('quotations')}
            className={`w-full text-left px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-2 ${activeTab === 'quotations'
              ? 'bg-white text-green-600 shadow-lg font-semibold'
              : 'text-indigo-100 hover:bg-indigo-700 hover:text-white'
              }`}
          >
            <span className="text-xs">📄</span>
            <div className="flex-1">
              <div className="text-sm font-medium">Quotations</div>
              <div className="text-xs opacity-75">{quotations.length} total</div>
            </div>
          </button>

          <button
            onClick={() => setActiveTab('followUps')}
            className={`w-full text-left px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-2 ${activeTab === 'followUps'
              ? 'bg-white text-purple-600 shadow-lg font-semibold'
              : 'text-indigo-100 hover:bg-indigo-700 hover:text-white'
              }`}
          >
            <span className="text-xs">📅</span>
            <div className="flex-1">
              <div className="text-sm font-medium">Follow-ups</div>
              <div className="text-xs opacity-75">Manage tasks</div>
            </div>
          </button>

          <div className="border-t border-indigo-700 my-3"></div>

          <button
            onClick={() => setShowTemplateSettings(true)}
            className="w-full text-left px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-2 text-indigo-100 hover:bg-indigo-700 hover:text-white"
          >
            <span className="text-xs">⚙️</span>
            <div className="text-sm font-medium">Templates</div>
          </button>

          <button
            onClick={() => setActiveTab('packages')}
            className={`w-full text-left px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-2 ${activeTab === 'packages'
              ? 'bg-white text-indigo-900 shadow-lg font-semibold'
              : 'text-indigo-100 hover:bg-indigo-700 hover:text-white'
              }`}
          >
            <span className="text-xs">📦</span>
            <div className="text-sm font-medium">Manage Packages</div>
          </button>

          <button
            onClick={() => setActiveTab('itinerary')}
            className={`w-full text-left px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-2 ${activeTab === 'itinerary'
              ? 'bg-white text-indigo-900 shadow-lg font-semibold'
              : 'text-indigo-100 hover:bg-indigo-700 hover:text-white'
              }`}
          >
            <span className="text-xs">📋</span>
            <div className="text-sm font-medium">Manage Itinerary</div>
          </button>

          <button
            onClick={() => setActiveTab('hotels')}
            className={`w-full text-left px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-2 ${activeTab === 'hotels'
              ? 'bg-white text-indigo-900 shadow-lg font-semibold'
              : 'text-indigo-100 hover:bg-indigo-700 hover:text-white'
              }`}
          >
            <span className="text-xs">🏨</span>
            <div className="text-sm font-medium">Manage Hotels</div>
          </button>

          <div className="mt-2 pt-2 border-t border-indigo-700">
            <button
              onClick={() => setShowGmailSettings(true)}
              className={`w-full text-left px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-2 ${gmailStatus.configured ? 'text-green-200 hover:bg-indigo-700 hover:text-white' : 'text-yellow-200 hover:bg-indigo-700 hover:text-white'}`}
            >
              <span className="text-xs">{gmailStatus.configured ? '\u2705' : '\u26a0\ufe0f'}</span>
              <div>
                <div className="text-sm font-medium">Gmail Settings</div>
                <div className="text-xs opacity-70">{gmailStatus.configured ? gmailStatus.email : 'Not configured'}</div>
              </div>
            </button>
          </div>
        </nav>

        {/* User Info & Logout */}
        <div className="p-3 border-t border-indigo-700 bg-indigo-950">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center">
              <span className="text-sm font-bold">{user?.name?.charAt(0) || 'U'}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold truncate">{user?.name || 'User'}</p>
              <p className="text-xs text-indigo-300 truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={() => {
              localStorage.clear();
              window.location.href = '/login';
            }}
            className="w-full px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all text-xs font-semibold flex items-center justify-center gap-2"
          >
            <span>🚪</span>
            <span>Logout</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="ml-56 flex-1 p-4 bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-3">
            <h1 className="text-lg font-bold text-gray-800">
              {activeTab === 'leads' && '📋 Leads Management'}
              {activeTab === 'quotations' && '📄 My Quotations'}
              {activeTab === 'revisions' && '🔄 Revision Requests'}
              {activeTab === 'completed' && '✅ Completed & Forwarded'}
              {activeTab === 'followUps' && '📅 Follow-up Tasks'}
            </h1>
            <p className="text-gray-600 text-xs mt-0.5">
              {activeTab === 'leads' && 'Manage and process tour booking leads'}
              {activeTab === 'quotations' && 'View and manage your quotations'}
              {activeTab === 'revisions' && 'Handle quotation revision requests from sales'}
              {activeTab === 'completed' && 'View completed revisions forwarded to sales'}
              {activeTab === 'followUps' && 'Manage your follow-up tasks and reminders'}
            </p>
          </div>


          {/* Stats Cards - Compact Professional Design */}
          {activeTab === 'leads' && (
            <div className="grid grid-cols-5 gap-2 mb-3">
              {/* Total Leads */}
              <div className="bg-white p-3 rounded-lg shadow-sm border-l-4 border-blue-500 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500 font-medium uppercase">{searchTerm ? 'Filtered' : 'Total'} Leads</p>
                    <p className="text-2xl font-bold text-gray-900">{filteredLeads.length}</p>
                    {searchTerm && leads.length !== filteredLeads.length && (
                      <p className="text-xs text-gray-400">of {leads.length}</p>
                    )}
                  </div>
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <span className="text-lg">📋</span>
                  </div>
                </div>
              </div>

              {/* Pending Vendor */}
              <div className="bg-white p-3 rounded-lg shadow-sm border-l-4 border-yellow-500 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500 font-medium uppercase">Pending Vendor</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {leads.filter(l => l.workflowStatus === 'vendor_approval_pending' || l.workflowStatus === 'forwarded_to_reservation').length}
                    </p>
                    <p className="text-xs text-yellow-600">Awaiting</p>
                  </div>
                  <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <span className="text-lg">⏳</span>
                  </div>
                </div>
              </div>

              {/* Ready to Quote */}
              <div className="bg-white p-3 rounded-lg shadow-sm border-l-4 border-orange-500 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500 font-medium uppercase">Ready to Quote</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {leads.filter(l => l.workflowStatus === 'vendor_approved' && (!l.quotations || l.quotations.length === 0)).length}
                    </p>
                    <p className="text-xs text-orange-600">Create now</p>
                  </div>
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                    <span className="text-lg">📝</span>
                  </div>
                </div>
              </div>

              {/* Draft Quotations */}
              <div className="bg-white p-3 rounded-lg shadow-sm border-l-4 border-indigo-500 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500 font-medium uppercase">Draft Quotations</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {leads.filter(l => l.quotations && l.quotations.some(q => !q.sentBy && q.status !== 'sent')).length}
                    </p>
                    <p className="text-xs text-indigo-600">Not sent</p>
                  </div>
                  <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <span className="text-lg">📄</span>
                  </div>
                </div>
              </div>

              {/* Sent to Sales */}
              <div className="bg-white p-3 rounded-lg shadow-sm border-l-4 border-green-500 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500 font-medium uppercase">Sent to Sales</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {leads.filter(l => l.workflowStatus === 'forwarded_to_sales' || (l.quotations && l.quotations.some(q => q.sentBy || q.status === 'sent'))).length}
                    </p>
                    <p className="text-xs text-green-600">Forwarded</p>
                  </div>
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <span className="text-lg">✅</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Search Box for Leads - Compact */}
          {activeTab === 'leads' && (
            <div className="mb-3">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search leads by name, email, phone, or destination..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 pl-9 border border-gray-200 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
                />
                <svg className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-2 text-gray-400 hover:text-gray-600 text-sm"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Leads Table */}
          {activeTab === 'leads' && (
            <div className="bg-white rounded-lg shadow overflow-visible">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Destination</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Created By</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Vendor Status</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Quotation Status</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredLeads.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                        {searchTerm ? (
                          <div>
                            <p className="text-lg font-medium">No leads found</p>
                            <p className="text-sm mt-1">Try a different search term</p>
                          </div>
                        ) : (
                          <div>
                            <p className="text-lg font-medium">No leads available</p>
                          </div>
                        )}
                      </td>
                    </tr>
                  ) : (
                    filteredLeads.map(lead => (
                      <tr key={lead._id} className="hover:bg-gray-50">
                        <td className="px-3 py-2">
                          <button
                            onClick={() => {
                              setSelectedLeadForProfile(lead);
                              setShowCustomerProfile(true);
                            }}
                            className="text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline text-left block"
                          >
                            {lead.customerName}
                          </button>
                          <div className="text-[10px] text-gray-400 truncate max-w-[120px]">{lead.email}</div>
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-700">{lead.destination}</td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1.5">
                            <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                              <span className="text-[10px] font-bold text-blue-700">
                                {(lead.createdByName || 'S').charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div className="min-w-0">
                              <p className="text-xs font-medium text-gray-900 truncate">
                                {lead.createdByName || 'Sales Team'}
                              </p>
                              <p className="text-[10px] text-gray-400">Sales</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full font-medium ${lead.workflowStatus === 'forwarded_to_sales' ? 'bg-green-100 text-green-700' :
                            lead.workflowStatus === 'vendor_approved' ? 'bg-blue-100 text-blue-700' :
                              lead.workflowStatus === 'vendor_approval_pending' ? 'bg-yellow-100 text-yellow-700' :
                                lead.workflowStatus === 'forwarded_to_reservation' ? 'bg-purple-100 text-purple-700' :
                                  'bg-gray-100 text-gray-700'
                            }`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${lead.workflowStatus === 'forwarded_to_sales' ? 'bg-green-500' :
                              lead.workflowStatus === 'vendor_approved' ? 'bg-blue-500' :
                                lead.workflowStatus === 'vendor_approval_pending' ? 'bg-yellow-500' :
                                  lead.workflowStatus === 'forwarded_to_reservation' ? 'bg-purple-500' :
                                    'bg-gray-500'
                              }`}></span>
                            {lead.workflowStatus === 'forwarded_to_sales' ? 'Sent' :
                              lead.workflowStatus === 'vendor_approved' ? 'Approved' :
                                lead.workflowStatus === 'vendor_approval_pending' ? 'Pending' :
                                  lead.workflowStatus === 'forwarded_to_reservation' ? 'New' :
                                    lead.workflowStatus?.replace(/_/g, ' ').substring(0, 10) || 'New'}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          {lead.vendorApprovals && lead.vendorApprovals.length > 0 ? (
                            <span className={`px-2 py-1 text-xs rounded-full ${lead.vendorApprovals[lead.vendorApprovals.length - 1].approvalStatus === 'approved'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                              }`}>
                              {lead.vendorApprovals[lead.vendorApprovals.length - 1].approvalStatus}
                            </span>
                          ) : (
                            <span className="text-gray-400">No vendor</span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {lead.quotations && lead.quotations.length > 0 ? (
                            <div className="space-y-1">
                              {/* Sent quotations - compact badge */}
                              {lead.quotations.filter(q => q.sentBy || q.status === 'sent').length > 0 && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                                  Sent ({lead.quotations.filter(q => q.sentBy || q.status === 'sent').length})
                                </span>
                              )}
                              {/* Draft quotations - compact badge */}
                              {lead.quotations.filter(q => !q.sentBy && q.status !== 'sent').length > 0 && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                                  <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                                  Draft ({lead.quotations.filter(q => !q.sentBy && q.status !== 'sent').length})
                                </span>
                              )}
                            </div>
                          ) : lead.workflowStatus === 'vendor_approved' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-medium rounded-full">
                              <span className="w-1.5 h-1.5 bg-orange-500 rounded-full animate-pulse"></span>
                              Ready to create
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <div className="relative">
                            {/* 3-Dot Menu Button */}
                            <button
                              onClick={(e) => { e.stopPropagation(); setOpenDropdownId(openDropdownId === lead._id ? null : lead._id); }}
                              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                              <svg className="w-5 h-5 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                              </svg>
                            </button>

                            {/* Dropdown Menu */}
                            {openDropdownId === lead._id && (
                              <div className="absolute right-0 bottom-full mb-1 w-52 bg-white rounded-lg shadow-xl border border-gray-200 z-[999] py-1">
                                {/* View Lead Details */}
                                <button
                                  onClick={() => { setOpenDropdownId(null); setSelectedLeadForProfile(lead); setShowCustomerProfile(true); }}
                                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 flex items-center gap-2"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                  View Lead Details
                                </button>

                                {/* Create Quotation */}
                                <button
                                  onClick={() => { setOpenDropdownId(null); setSelectedLeadForQuotation(lead); setShowQuotationForm(true); }}
                                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-600 flex items-center gap-2"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                  {lead.quotations?.length > 0 ? `Create Quotation (${lead.quotations.length})` : 'Create Quotation'}
                                </button>

                                <div className="border-t border-gray-100 my-1"></div>

                                {/* Send to Vendor */}
                                <button
                                  onClick={() => {
                                    setOpenDropdownId(null);
                                    openVendorEmailForLead(lead);
                                  }}
                                  className="w-full px-4 py-2 text-left text-sm text-blue-600 hover:bg-blue-50 flex items-center gap-2"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                                  Send to Vendor
                                </button>

                                {lead.workflowStatus === 'vendor_approval_pending' && (
                                  <button
                                    onClick={() => { setOpenDropdownId(null); setSelectedLead(lead); setVendorStatusData({ status: 'approved', notes: '' }); setShowVendorStatusModal(true); }}
                                    className="w-full px-4 py-2 text-left text-sm text-green-600 hover:bg-green-50 flex items-center gap-2"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                    Update Vendor Status
                                  </button>
                                )}

                                <div className="border-t border-gray-100 my-1"></div>

                                {/* Delete Lead */}
                                <button
                                  onClick={async () => {
                                    setOpenDropdownId(null);
                                    if (!window.confirm(`Delete lead for ${lead.customerName}?`)) return;
                                    try {
                                      const token = localStorage.getItem('token') || localStorage.getItem('companyToken') || localStorage.getItem('employeeToken');
                                      await axios.delete(`/leads/${lead._id}`, { headers: { Authorization: `Bearer ${token}` } });
                                      toast.success('Deleted successfully'); fetchLeads(); if (activeTab === 'quotations') fetchQuotations();
                                    } catch (error) { toast.error('Error occurred'); }
                                  }}
                                  className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                  Delete Lead
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Quotations Table */}
          {activeTab === 'quotations' && (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="p-4 border-b bg-gray-50 flex flex-wrap items-center justify-between gap-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                    📋 My Quotations
                  </h2>
                  <p className="text-sm text-gray-500">View and manage your quotations</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setQuotationFilter('all')}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${quotationFilter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                  >
                    📊 All Quotations ({quotations.length})
                  </button>
                  <button
                    onClick={() => setQuotationFilter('sent')}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${quotationFilter === 'sent' ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                  >
                    ✅ Sent ({quotations.filter(q => q.sentToSales || q.status === 'sent').length})
                  </button>
                  <button
                    onClick={() => setQuotationFilter('not_sent')}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${quotationFilter === 'not_sent' ? 'bg-gray-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                  >
                    📝 Not Sent ({quotations.filter(q => !q.sentToSales && q.status !== 'sent').length})
                  </button>
                  <button
                    onClick={() => setQuotationFilter('amendment')}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${quotationFilter === 'amendment' ? 'bg-orange-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                  >
                    ⚠️ Amendments ({quotations.filter(q => q.amendmentRequests?.length > 0 || q.status === 'amendment_requested').length})
                  </button>
                </div>
              </div>

              {/* Search */}
              <div className="p-3 border-b">
                <input
                  type="text"
                  placeholder="Search quotations..."
                  value={quotationSearchTerm}
                  onChange={(e) => setQuotationSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg text-sm"
                />
              </div>

              {/* Stats */}
              <div className="grid grid-cols-5 gap-4 p-4 bg-gray-50 border-b">
                <div className="bg-white p-3 rounded-lg border-l-4 border-blue-500">
                  <div className="text-xs text-gray-500">TOTAL</div>
                  <div className="text-xl font-bold">{quotations.length}</div>
                </div>
                <div className="bg-white p-3 rounded-lg border-l-4 border-yellow-500">
                  <div className="text-xs text-gray-500">DRAFT</div>
                  <div className="text-xl font-bold text-yellow-600">{quotations.filter(q => !q.sentToSales && q.status !== 'sent').length}</div>
                </div>
                <div className="bg-white p-3 rounded-lg border-l-4 border-green-500">
                  <div className="text-xs text-gray-500">SENT</div>
                  <div className="text-xl font-bold text-green-600">{quotations.filter(q => q.sentToSales || q.status === 'sent').length}</div>
                </div>
                <div className="bg-white p-3 rounded-lg border-l-4 border-purple-500">
                  <div className="text-xs text-gray-500">ACCEPTED</div>
                  <div className="text-xl font-bold text-purple-600">{quotations.filter(q => q.status === 'accepted').length}</div>
                </div>
                <div className="bg-white p-3 rounded-lg border-l-4 border-orange-500">
                  <div className="text-xs text-gray-500">AMENDMENTS</div>
                  <div className="text-xl font-bold text-orange-600">{quotations.filter(q => q.amendmentRequests?.length > 0).length}</div>
                </div>
              </div>

              <table className="min-w-full">
                <thead className="bg-gradient-to-r from-indigo-600 to-purple-600">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-bold text-white uppercase">Tour Code</th>
                    <th className="px-3 py-2 text-left text-xs font-bold text-white uppercase">Customer</th>
                    <th className="px-3 py-2 text-left text-xs font-bold text-white uppercase">Destination</th>
                    <th className="px-3 py-2 text-left text-xs font-bold text-white uppercase">Travel Date</th>
                    <th className="px-3 py-2 text-left text-xs font-bold text-white uppercase">Total Price</th>
                    <th className="px-3 py-2 text-left text-xs font-bold text-white uppercase">Status</th>
                    <th className="px-3 py-2 text-left text-xs font-bold text-white uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loadingQuotations ? (
                    <tr><td colSpan="7" className="px-6 py-8 text-center text-gray-500">Loading...</td></tr>
                  ) : quotations.length === 0 ? (
                    <tr><td colSpan="7" className="px-6 py-8 text-center text-gray-500">No quotations found</td></tr>
                  ) : (
                    quotations
                      .filter(q => {
                        if (quotationFilter === 'sent') return q.sentToSales || q.status === 'sent';
                        if (quotationFilter === 'not_sent') return !q.sentToSales && q.status !== 'sent';
                        if (quotationFilter === 'amendment') return q.amendmentRequests?.length > 0 || q.status === 'amendment_requested';
                        return true;
                      })
                      .filter(q => {
                        if (!quotationSearchTerm) return true;
                        const search = quotationSearchTerm.toLowerCase();
                        return (
                          q.tourCode?.toLowerCase().includes(search) ||
                          q.customerName?.toLowerCase().includes(search) ||
                          q.customerEmail?.toLowerCase().includes(search) ||
                          q.destination?.toLowerCase().includes(search)
                        );
                      })
                      .map((quotation) => (
                        <tr key={quotation._id} className="hover:bg-gray-50">
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-2">
                              {quotation.packageImage && (
                                <img src={quotation.packageImage} alt="" className="w-10 h-8 object-cover rounded" />
                              )}
                              <span className="text-blue-600 font-medium text-sm">{quotation.tourCode || quotation.quotationNumber || 'N/A'}</span>
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <div className="text-sm font-medium text-gray-900">{quotation.customerName}</div>
                            <div className="text-xs text-gray-500 truncate max-w-[120px]">{quotation.customerEmail}</div>
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-700">{quotation.destination}</td>
                          <td className="px-3 py-2 text-sm text-gray-700">{new Date(quotation.travelDate || quotation.createdAt).toLocaleDateString()}</td>
                          <td className="px-3 py-2">
                            <span className="font-semibold text-green-600">₹{(quotation.totalPrice || quotation.pricingBreakdown?.grandTotal || quotation.items?.reduce((sum, item) => sum + (parseFloat(item.totalPrice) || 0), 0) || quotation.packages?.reduce((sum, pkg) => sum + (parseFloat(pkg.totalPrice) || 0), 0) || 0).toLocaleString()}</span>
                          </td>
                          <td className="px-3 py-2">
                            <div className="space-y-1">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${quotation.status === 'accepted' ? 'bg-green-100 text-green-700' : quotation.status === 'rejected' ? 'bg-red-100 text-red-700' : quotation.status === 'amendment_requested' ? 'bg-orange-100 text-orange-700' : quotation.status === 'sent' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${quotation.status === 'accepted' ? 'bg-green-500' : quotation.status === 'rejected' ? 'bg-red-500' : quotation.status === 'amendment_requested' ? 'bg-orange-500' : quotation.status === 'sent' ? 'bg-blue-500' : 'bg-gray-400'}`}></span>
                                {quotation.status === 'accepted' ? 'Accepted' : quotation.status === 'rejected' ? 'Rejected' : quotation.status === 'amendment_requested' ? 'Amendment' : quotation.status === 'sent' ? 'Sent to Sales' : 'Draft'}
                              </span>
                              {quotation.amendmentRequests?.length > 0 && (<div className="text-[10px] text-orange-600">⚠️ {quotation.amendmentRequests.length} Amendment</div>)}
                              {quotation.sentDate && (<div className="text-[10px] text-gray-500">✓ {new Date(quotation.sentDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</div>)}
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <div className="relative">
                              <button
                                onClick={(e) => { e.stopPropagation(); setOpenDropdownId(openDropdownId === quotation._id ? null : quotation._id); }}
                                className="p-2 hover:bg-gray-100 rounded-lg"
                              >
                                <svg className="w-5 h-5 text-gray-500" fill="currentColor" viewBox="0 0 20 20"><path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" /></svg>
                              </button>
                              {openDropdownId === quotation._id && (
                                <div className="absolute right-0 bottom-full mb-1 w-48 bg-white rounded-lg shadow-xl border z-[999] py-1">
                                  <button onClick={async () => {
                                    setOpenDropdownId(null);
                                    const token = localStorage.getItem('token') || localStorage.getItem('companyToken') || localStorage.getItem('employeeToken');
                                    const response = await axios.get(`/leads/${quotation.leadId}`, { headers: { Authorization: `Bearer ${token}` } });
                                    if (response.data) {
                                      const lead = response.data;
                                      const fullQuotation = lead.quotations.find(q => q._id.toString() === quotation._id.toString());
                                      if (fullQuotation) { setQuotationData(fullQuotation); setSelectedLeadForQuotation(lead); setShowQuotationPDF(true); }
                                    }
                                  }} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-blue-50 flex items-center gap-2">👁 View Quotation</button>

                                  {/* View Amendment Details - if any */}
                                  {quotation.amendmentRequests?.length > 0 && (
                                    <button onClick={() => {
                                      setOpenDropdownId(null);
                                      const latestAmendment = quotation.amendmentRequests[quotation.amendmentRequests.length - 1];
                                      setSelectedAmendment({
                                        ...latestAmendment,
                                        quotationNumber: quotation.quotationNumber,
                                        customerName: quotation.customerName,
                                        destination: quotation.destination,
                                        allAmendments: quotation.amendmentRequests
                                      });
                                    }} className="w-full px-4 py-2 text-left text-sm text-orange-600 hover:bg-orange-50 flex items-center gap-2">
                                      📝 View Amendment ({quotation.amendmentRequests.length})
                                    </button>
                                  )}

                                  <button onClick={async () => {
                                    setOpenDropdownId(null);
                                    const token = localStorage.getItem('token') || localStorage.getItem('companyToken') || localStorage.getItem('employeeToken');
                                    const response = await axios.get(`/leads/${quotation.leadId}`, { headers: { Authorization: `Bearer ${token}` } });
                                    if (response.data) {
                                      const lead = response.data;
                                      const fullQuotation = lead.quotations.find(q => q._id.toString() === quotation._id.toString());
                                      if (fullQuotation) { setQuotationData(fullQuotation); setSelectedLeadForQuotation(lead); setShowQuotationForm(true); }
                                    }
                                  }} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-yellow-50 flex items-center gap-2">✏️ Edit Quotation</button>
                                  <div className="border-t my-1"></div>
                                  {(!quotation.sentToSales && quotation.status !== 'sent') || quotation.amendmentRequests?.length > 0 ? (
                                    <button onClick={async () => {
                                      setOpenDropdownId(null);
                                      if (!window.confirm('Send to Sales?')) return;
                                      const token = localStorage.getItem('token') || localStorage.getItem('companyToken') || localStorage.getItem('employeeToken');
                                      await axios.post(`/leads/${quotation.leadId}/send-quotation-to-sales`, { quotationId: quotation._id }, { headers: { Authorization: `Bearer ${token}` } });
                                      toast.success('Sent successfully!'); fetchQuotations(); fetchLeads();
                                    }} className="w-full px-4 py-2 text-left text-sm text-purple-600 hover:bg-purple-50 flex items-center gap-2">📤 Send to Sales</button>
                                  ) : (
                                    <div className="px-4 py-2 text-sm text-green-600 flex items-center gap-2">✅ Already Sent</div>
                                  )}
                                  <button onClick={() => { setOpenDropdownId(null); handleResendToVendor(quotation); }} className="w-full px-4 py-2 text-left text-sm text-blue-600 hover:bg-blue-50 flex items-center gap-2">📧 Send to Vendor</button>
                                  <button onClick={() => {
                                    setOpenDropdownId(null);
                                    setAmendmentFormData({ quotation, message: '', urgency: 'normal', type: 'change_request' });
                                    setShowAmendmentForm(true);
                                  }} className="w-full px-4 py-2 text-left text-sm text-orange-600 hover:bg-orange-50 flex items-center gap-2">⚠️ Send Amendment to Client</button>
                                  <div className="border-t my-1"></div>
                                  <button onClick={() => { setOpenDropdownId(null); handleDeleteQuotation(quotation); }} className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2">🗑️ Delete</button>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Revision Requests Table */}
          {activeTab === 'revisions' && (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-3 py-2">
                <h2 className="text-xl font-bold">🔄 Revision Requests from Sales</h2>
              </div>
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Quotation ID</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Destination</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Requested At</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Urgency</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {loadingRevisions ? (
                    <tr><td colSpan="6" className="text-center py-8">Loading...</td></tr>
                  ) : revisionRequests.length === 0 ? (
                    <tr><td colSpan="6" className="text-center py-8 text-gray-500">No revision requests</td></tr>
                  ) : (
                    revisionRequests.map((request) => (
                      <tr key={request._id} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-blue-600 font-medium">{request.quotation?.tourCode || 'N/A'}</td>
                        <td className="px-3 py-2">{request.quotation?.customerName}</td>
                        <td className="px-3 py-2">{request.quotation?.destination}</td>
                        <td className="px-3 py-2">{new Date(request.createdAt).toLocaleDateString()}</td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-1 rounded text-xs ${request.urgency === 'high' ? 'bg-red-100 text-red-700' : request.urgency === 'medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}`}>
                            {request.urgency || 'Normal'}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <button className="text-blue-600 hover:text-blue-800 text-sm">View Details</button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Follow-ups */}
          {activeTab === 'followUps' && <FollowUpManager />}

          {/* Template Settings */}
          {activeTab === 'templates' && (
            <TemplateSettings
              onClose={() => setActiveTab('leads')}
            />
          )}

          {/* Package Management */}
          {activeTab === 'packages' && (
            <TourPackageManagement />
          )}

          {/* Itinerary Management */}
          {activeTab === 'itinerary' && (
            <ItineraryManagement />
          )}

          {/* Hotel Management */}
          {activeTab === 'hotels' && (
            <HotelManagement />
          )}
        </div>
      </div>

      {/* Vendor Status Modal */}
      {showVendorStatusModal && selectedLead && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-90vw">
            <h3 className="text-lg font-bold mb-4">Update Vendor Status</h3>
            <p className="text-sm text-gray-600 mb-4">Lead: {selectedLead.customerName} - {selectedLead.destination}</p>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Status</label>
              <select
                value={vendorStatusData.status}
                onChange={(e) => setVendorStatusData({ ...vendorStatusData, status: e.target.value })}
                className="w-full border rounded-lg px-3 py-2"
              >
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Notes (Optional)</label>
              <textarea
                value={vendorStatusData.notes}
                onChange={(e) => setVendorStatusData({ ...vendorStatusData, notes: e.target.value })}
                className="w-full border rounded-lg px-3 py-2"
                rows={3}
                placeholder="Add any notes..."
              />
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowVendorStatusModal(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button onClick={updateVendorStatus} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Update</button>
            </div>
          </div>
        </div>
      )}

      {/* Customer Profile Modal */}
      {showCustomerProfile && selectedLeadForProfile && (
        <CustomerProfileModal
          lead={selectedLeadForProfile}
          onClose={() => { setShowCustomerProfile(false); setSelectedLeadForProfile(null); }}
        />
      )}

      {/* Quotation Form Modal */}
      {showQuotationForm && selectedLeadForQuotation && (
        <QuotationForm
          lead={selectedLeadForQuotation}
          onClose={() => { setShowQuotationForm(false); setSelectedLeadForQuotation(null); setQuotationData(null); }}
          onSuccess={(data) => {
            setQuotationData(data);
            setShowQuotationForm(false);
            setShowQuotationPDF(true);
            fetchLeads();
            fetchQuotations();
          }}
          existingQuotation={quotationData}
        />
      )}

      {/* Quotation PDF Modal */}
      {showQuotationPDF && quotationData && (
        <TourQuotationPDF
          quotation={quotationData}
          lead={selectedLeadForQuotation}
          onClose={() => { setShowQuotationPDF(false); setQuotationData(null); }}
          onEdit={() => { setShowQuotationPDF(false); setShowQuotationForm(true); }}
          onSendToSales={async () => {
            try {
              const token = localStorage.getItem('token') ||
                localStorage.getItem('employeeToken') ||
                localStorage.getItem('companyToken');

              // Use correct endpoint: POST /leads/:id/send-quotation-to-sales
              await axios.post(
                `/leads/${selectedLeadForQuotation._id}/send-quotation-to-sales`,
                { quotationId: quotationData._id },
                { headers: { Authorization: `Bearer ${token}` } }
              );

              toast.success('Quotation sent to Sales successfully!');
              setShowQuotationPDF(false);
              setQuotationData(null);
              fetchQuotations();
              fetchLeads();
            } catch (error) {
              console.error('Error sending to sales:', error);
              toast.error('Error sending to sales: ' + (error.response?.data?.message || error.message));
            }
          }}
        />
      )}

      {/* Amendment Details Modal */}
      {selectedAmendment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4">
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-4 rounded-t-xl flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold flex items-center gap-2">
                  📝 Amendment Request Details
                </h2>
                <p className="text-sm text-orange-100 mt-1">
                  Requested by Sales Team
                </p>
              </div>
              <button
                onClick={() => setSelectedAmendment(null)}
                className="text-white hover:text-gray-200 text-2xl"
              >
                ✕
              </button>
            </div>

            <div className="p-6">
              {/* Quotation Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-blue-900 mb-2">Quotation Info</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-600">Quotation:</span>
                    <span className="ml-2 font-medium">{selectedAmendment.quotationNumber || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Customer:</span>
                    <span className="ml-2 font-medium">{selectedAmendment.customerName}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Destination:</span>
                    <span className="ml-2 font-medium">{selectedAmendment.destination}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Urgency:</span>
                    <span className={`ml-2 px-2 py-0.5 rounded text-xs font-semibold ${selectedAmendment.urgency === 'high' ? 'bg-red-100 text-red-700' :
                      selectedAmendment.urgency === 'normal' ? 'bg-blue-100 text-blue-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                      {(selectedAmendment.urgency || 'Normal').toUpperCase()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Amendment Details */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                  🔄 Changes Requested
                </h3>

                {/* All Amendments History */}
                {selectedAmendment.allAmendments?.map((amendment, index) => (
                  <div key={index} className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-xs font-semibold text-orange-700 bg-orange-100 px-2 py-0.5 rounded">
                        Amendment #{index + 1}
                      </span>
                      <span className="text-xs text-gray-500">
                        {new Date(amendment.requestedAt).toLocaleDateString('en-IN', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>

                    {/* The actual changes message */}
                    <div className="bg-white border border-orange-100 rounded p-3 mt-2">
                      <p className="text-gray-800 whitespace-pre-wrap">{amendment.changes || amendment.message || 'No details provided'}</p>
                    </div>

                    {/* Additional notes if any */}
                    {amendment.notes && (
                      <div className="mt-2 text-sm text-gray-600">
                        <span className="font-medium">Notes: </span>{amendment.notes}
                      </div>
                    )}

                    {/* Requested by */}
                    {amendment.requestedBy && (
                      <div className="mt-2 text-xs text-gray-500">
                        Requested by: {amendment.requestedByName || amendment.requestedBy}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Action Button */}
              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => setSelectedAmendment(null)}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    // Navigate to edit quotation
                    setSelectedAmendment(null);
                    setActiveTab('quotations');
                    toast.info('Go to the quotation and click "Edit" to make changes');
                  }}
                  className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium"
                >
                  ✏️ Edit Quotation
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Send Amendment to Client Modal */}
      {showAmendmentForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowAmendmentForm(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-4 rounded-t-2xl flex justify-between items-center">
              <h3 className="text-lg font-bold flex items-center gap-2">⚠️ Send Amendment to Client</h3>
              <button onClick={() => setShowAmendmentForm(false)} className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1">✕</button>
            </div>
            <div className="p-6">
              {/* Quotation Info */}
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4">
                <div className="text-sm text-gray-600">
                  <span className="font-semibold">Customer:</span> {amendmentFormData.quotation?.customerName || 'N/A'}
                </div>
                <div className="text-sm text-gray-600">
                  <span className="font-semibold">Destination:</span> {amendmentFormData.quotation?.destination || 'N/A'}
                </div>
                <div className="text-sm text-gray-600">
                  <span className="font-semibold">Quotation:</span> {amendmentFormData.quotation?.quotationNumber || 'N/A'}
                </div>
              </div>

              {/* Amendment Type */}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-1">Amendment Type</label>
                <select
                  value={amendmentFormData.type}
                  onChange={e => setAmendmentFormData(prev => ({ ...prev, type: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-orange-300 focus:border-orange-400"
                >
                  <option value="change_request">📝 Change Request</option>
                  <option value="date_change">📅 Date Change</option>
                  <option value="hotel_change">🏨 Hotel Change</option>
                  <option value="price_revision">💰 Price Revision</option>
                  <option value="itinerary_change">🗺️ Itinerary Change</option>
                  <option value="cancellation">❌ Cancellation</option>
                  <option value="other">📋 Other</option>
                </select>
              </div>

              {/* Urgency */}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-1">Urgency</label>
                <div className="flex gap-2">
                  {['normal', 'urgent', 'critical'].map(level => (
                    <button
                      key={level}
                      onClick={() => setAmendmentFormData(prev => ({ ...prev, urgency: level }))}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors border-2 ${amendmentFormData.urgency === level
                        ? level === 'critical' ? 'bg-red-600 text-white border-red-600'
                          : level === 'urgent' ? 'bg-orange-500 text-white border-orange-500'
                            : 'bg-blue-500 text-white border-blue-500'
                        : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                        }`}
                    >
                      {level === 'critical' ? '🔴' : level === 'urgent' ? '🟠' : '🔵'} {level.charAt(0).toUpperCase() + level.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Message */}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-1">Amendment Details *</label>
                <textarea
                  value={amendmentFormData.message}
                  onChange={e => setAmendmentFormData(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Describe the changes needed... e.g., Change hotel from 3-star to 5-star, Update travel dates, etc."
                  rows="5"
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-orange-300 focus:border-orange-400 resize-none"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowAmendmentForm(false)}
                  className="flex-1 px-4 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!amendmentFormData.message.trim()) {
                      toast.warning('Please enter amendment details');
                      return;
                    }
                    const q = amendmentFormData.quotation;
                    const typeLabels = {
                      change_request: 'Change Request', date_change: 'Date Change', hotel_change: 'Hotel Change',
                      price_revision: 'Price Revision', itinerary_change: 'Itinerary Change', cancellation: 'Cancellation', other: 'Other'
                    };

                    // Save amendment to DB
                    try {
                      const token = localStorage.getItem('token') || localStorage.getItem('companyToken') || localStorage.getItem('employeeToken');
                      await axios.post(`/leads/${q.leadId}/request-quotation-revision`, {
                        quotationId: q._id,
                        changes: amendmentFormData.message,
                        urgency: amendmentFormData.urgency,
                        notes: `Type: ${typeLabels[amendmentFormData.type]}`,
                        requestedBy: localStorage.getItem('userId') || 'reservation',
                        requestedByName: localStorage.getItem('userName') || 'Reservation Team'
                      }, { headers: { Authorization: `Bearer ${token}` } });
                    } catch (err) {
                      console.log('Amendment save note:', err.message);
                    }

                    // Open Gmail compose
                    const subject = encodeURIComponent(`⚠️ Amendment Request - ${q.quotationNumber || q.tourCode || ''} - ${q.customerName || ''}`);
                    const body = encodeURIComponent(
                      `Dear ${q.customerName || 'Client'},

Greetings from Gajanan World Tourism!

We would like to inform you about an amendment regarding your upcoming trip.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
📋 AMENDMENT DETAILS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Destination: ${q.destination || 'N/A'}
Quotation: ${q.quotationNumber || 'N/A'}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
📝 CHANGES
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${amendmentFormData.message}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Please review the above changes and let us know if you have any questions.

Best regards,
Gajanan World Tourism
Reservation Team`
                    );
                    const clientEmail = q.email || '';
                    window.open(`https://mail.google.com/mail/?view=cm&fs=1&to=${clientEmail}&su=${subject}&body=${body}`, '_blank');
                    toast.success('Amendment email opened in Gmail!');
                    setShowAmendmentForm(false);
                    fetchQuotations();
                  }}
                  className="flex-1 px-4 py-2.5 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg hover:from-orange-600 hover:to-red-600 font-medium text-sm flex items-center justify-center gap-2"
                >
                  📧 Send via Email
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Vendor Email Modal */}
      {showVendorEmailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => { setVendorEmailDropdownOpen(false); }}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-4 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold flex items-center gap-2">📧 Send to Vendor</h2>
                <p className="text-sm text-blue-100 mt-1">
                  {vendorContacts.length > 0 ? `${vendorContacts.length} saved vendor${vendorContacts.length > 1 ? 's' : ''} available` : 'Vendor email direct bhejo'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { setShowManageVendors(true); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg text-sm font-medium transition-colors"
                  title="Vendor contacts manage karo"
                >
                  👥 Manage Vendors
                </button>
                <button onClick={() => { setShowVendorEmailModal(false); setVendorEmailDropdownOpen(false); }} className="text-white hover:text-gray-200 text-2xl">✕</button>
              </div>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
              {/* Vendor Email — Dropdown + Manual input */}
              <div className="mb-5">
                <label className="block text-sm font-semibold text-gray-700 mb-2">📩 Vendor Email Address *</label>

                {/* Dropdown selector (only if contacts exist) */}
                {vendorContacts.length > 0 && (
                  <div className="relative mb-2">
                    <button
                      type="button"
                      onClick={() => setVendorEmailDropdownOpen(prev => !prev)}
                      className="w-full flex items-center justify-between px-4 py-2.5 bg-blue-50 border-2 border-blue-200 rounded-lg hover:border-blue-400 transition-colors text-left"
                    >
                      <span className="flex items-center gap-2">
                        <span className="text-blue-600 text-lg">👥</span>
                        <span className="text-sm text-gray-600">Saved vendors se choose karo...</span>
                      </span>
                      <svg className={`w-4 h-4 text-blue-500 transition-transform ${vendorEmailDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                    </button>

                    {vendorEmailDropdownOpen && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl shadow-2xl border border-gray-200 z-[200] overflow-hidden">
                        <div className="p-2 border-b bg-gray-50">
                          <p className="text-xs text-gray-500 font-medium px-2">📋 Saved Vendor Contacts</p>
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          {vendorContacts.map(vc => (
                            <button
                              key={vc._id}
                              type="button"
                              onClick={() => {
                                setVendorEmailData(prev => ({ ...prev, vendorEmail: vc.email }));
                                setVendorEmailDropdownOpen(false);
                              }}
                              className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-blue-50 transition-colors ${vendorEmailData.vendorEmail === vc.email ? 'bg-blue-50 border-l-4 border-blue-500' : ''}`}
                            >
                              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                                {vc.name.charAt(0).toUpperCase()}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-gray-800 text-sm truncate">{vc.name}</div>
                                <div className="text-xs text-blue-600 truncate">{vc.email}</div>
                              </div>
                              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${vc.category === 'Hotel' ? 'bg-purple-100 text-purple-700' :
                                  vc.category === 'Transport' ? 'bg-green-100 text-green-700' :
                                    vc.category === 'Flight' ? 'bg-blue-100 text-blue-700' :
                                      'bg-gray-100 text-gray-600'
                                }`}>{vc.category}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Manual / selected email display */}
                <input
                  type="email"
                  value={vendorEmailData.vendorEmail}
                  onChange={(e) => setVendorEmailData(prev => ({ ...prev, vendorEmail: e.target.value }))}
                  placeholder={vendorContacts.length > 0 ? 'Ya directly type karo email...' : 'vendor@example.com'}
                  className="w-full px-4 py-3 border-2 border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
                  autoFocus
                />

                {vendorContacts.length === 0 && (
                  <p className="mt-1.5 text-xs text-gray-400">
                    💡 <button type="button" onClick={() => setShowManageVendors(true)} className="text-blue-500 hover:underline">Vendor contacts add karo</button> toh agle baar dropdown se choose kar sakte ho
                  </p>
                )}
              </div>

              {/* Subject */}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">📝 Subject</label>
                <input
                  type="text"
                  value={vendorEmailData.subject}
                  onChange={(e) => setVendorEmailData(prev => ({ ...prev, subject: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Email Body */}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">📄 Email Body</label>
                <textarea
                  value={vendorEmailData.body}
                  onChange={(e) => setVendorEmailData(prev => ({ ...prev, body: e.target.value }))}
                  rows={12}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="border-t bg-gray-50 px-6 py-4">
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={handleSendVendorEmail}
                  disabled={sendingVendorEmail || !vendorEmailData.vendorEmail}
                  className="flex-1 min-w-[140px] px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold flex items-center justify-center gap-2 transition-colors"
                >
                  {sendingVendorEmail ? (<><span className="animate-spin">⏳</span> Sending...</>) : (<>📤 Send Email</>)}
                </button>
                <button
                  onClick={() => {
                    const fullText = `To: ${vendorEmailData.vendorEmail || '(enter email)'}\nSubject: ${vendorEmailData.subject}\n\n${vendorEmailData.body}`;
                    navigator.clipboard.writeText(fullText).then(() => toast.success('📋 Copied!')).catch(() => { const t = document.createElement('textarea'); t.value = fullText; document.body.appendChild(t); t.select(); document.execCommand('copy'); document.body.removeChild(t); toast.success('📋 Copied!'); });
                  }}
                  className="px-4 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-medium flex items-center gap-2 transition-colors"
                >📋 Copy</button>
                <button
                  onClick={() => { window.open(`https://wa.me/?text=${encodeURIComponent(`${vendorEmailData.subject}\n\n${vendorEmailData.body}`)}`, '_blank'); }}
                  className="px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center gap-2 transition-colors"
                >💬 WhatsApp</button>
                <button
                  onClick={() => { window.open(`mailto:${vendorEmailData.vendorEmail || ''}?subject=${encodeURIComponent(vendorEmailData.subject)}&body=${encodeURIComponent(vendorEmailData.body)}`, '_blank'); }}
                  className="px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium flex items-center gap-2 transition-colors"
                >📨 Email App</button>
                <button
                  onClick={() => { setShowVendorEmailModal(false); setVendorEmailDropdownOpen(false); }}
                  className="px-4 py-3 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 font-medium transition-colors"
                >Cancel</button>
              </div>
              <p className="text-xs text-gray-500 mt-2">💡 "Send Email" kaam na kare toh Copy ya WhatsApp use karo.</p>
            </div>
          </div>
        </div>
      )}

      {/* Manage Vendor Contacts Modal */}
      {showManageVendors && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white px-6 py-4 flex justify-between items-center flex-shrink-0">
              <div>
                <h2 className="text-xl font-bold flex items-center gap-2">👥 Vendor Contacts</h2>
                <p className="text-sm text-indigo-100 mt-0.5">{vendorContacts.length} saved contact{vendorContacts.length !== 1 ? 's' : ''}</p>
              </div>
              <button onClick={() => setShowManageVendors(false)} className="text-white hover:text-gray-200 text-2xl">✕</button>
            </div>

            {/* Add New Vendor Form */}
            <div className="p-5 border-b bg-indigo-50 flex-shrink-0">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">➕ Naya Vendor Add Karo</h3>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <input
                  type="text"
                  placeholder="Vendor Name *"
                  value={newVendor.name}
                  onChange={e => setNewVendor(prev => ({ ...prev, name: e.target.value }))}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400"
                />
                <input
                  type="email"
                  placeholder="Email Address *"
                  value={newVendor.email}
                  onChange={e => setNewVendor(prev => ({ ...prev, email: e.target.value }))}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400"
                />
                <input
                  type="tel"
                  placeholder="Phone (optional)"
                  value={newVendor.phone}
                  onChange={e => setNewVendor(prev => ({ ...prev, phone: e.target.value }))}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400"
                />
                <select
                  value={newVendor.category}
                  onChange={e => setNewVendor(prev => ({ ...prev, category: e.target.value }))}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 bg-white"
                >
                  {['Hotel', 'Transport', 'Flight', 'Tour Operator', 'Restaurant', 'Activity', 'Other'].map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <button
                onClick={handleAddVendor}
                disabled={savingVendor}
                className="w-full py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-semibold text-sm flex items-center justify-center gap-2 transition-colors"
              >
                {savingVendor ? (<><span className="animate-spin">⏳</span> Saving...</>) : (<>✅ Add Vendor</>)}
              </button>
            </div>

            {/* Contacts List */}
            <div className="overflow-y-auto flex-1 p-4">
              {vendorContacts.length === 0 ? (
                <div className="text-center py-10 text-gray-400">
                  <div className="text-5xl mb-3">📭</div>
                  <p className="font-medium">Koi vendor contacts nahi hain</p>
                  <p className="text-sm mt-1">Upar form se pehla vendor add karo</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {vendorContacts.map(vc => (
                    <div key={vc._id} className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-xl hover:border-indigo-300 hover:shadow-sm transition-all">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center text-white font-bold flex-shrink-0">
                        {vc.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-gray-800 text-sm">{vc.name}</div>
                        <div className="text-xs text-blue-600 truncate">{vc.email}</div>
                        {vc.phone && <div className="text-xs text-gray-400">{vc.phone}</div>}
                      </div>
                      <span className={`text-[10px] px-2 py-1 rounded-full font-semibold flex-shrink-0 ${vc.category === 'Hotel' ? 'bg-purple-100 text-purple-700' :
                          vc.category === 'Transport' ? 'bg-green-100 text-green-700' :
                            vc.category === 'Flight' ? 'bg-blue-100 text-blue-700' :
                              vc.category === 'Tour Operator' ? 'bg-orange-100 text-orange-700' :
                                'bg-gray-100 text-gray-600'
                        }`}>{vc.category}</span>
                      <button
                        onClick={() => handleDeleteVendor(vc._id, vc.name)}
                        className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                        title="Delete karo"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t bg-gray-50 flex-shrink-0">
              <button
                onClick={() => setShowManageVendors(false)}
                className="w-full py-2.5 bg-gray-800 text-white rounded-lg hover:bg-gray-900 font-medium text-sm transition-colors"
              >
                ✓ Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Gmail Settings Modal */}
      {showGmailSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="bg-gradient-to-r from-red-500 to-orange-500 text-white px-6 py-4 rounded-t-xl flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold flex items-center gap-2">📧 Gmail Settings</h2>
                <p className="text-sm text-red-100 mt-1">Configure email for Send to Vendor</p>
              </div>
              <button onClick={() => setShowGmailSettings(false)} className="text-white hover:text-gray-200 text-2xl">✕</button>
            </div>

            <div className="p-6">
              {gmailStatus.configured && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 flex items-center gap-2">
                  <span className="text-green-600 text-lg">✅</span>
                  <div>
                    <p className="text-sm font-semibold text-green-800">Gmail Connected</p>
                    <p className="text-xs text-green-600">{gmailStatus.email}</p>
                  </div>
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Gmail Email Address</label>
                <input
                  type="email"
                  value={gmailSettings.emailUser}
                  onChange={(e) => setGmailSettings(prev => ({ ...prev, emailUser: e.target.value }))}
                  placeholder="your-email@gmail.com"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Gmail App Password</label>
                <input
                  type="password"
                  value={gmailSettings.emailPass}
                  onChange={(e) => setGmailSettings(prev => ({ ...prev, emailPass: e.target.value }))}
                  placeholder="16-character app password"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                <p className="text-xs font-semibold text-amber-800 mb-1">⚠️ How to get App Password:</p>
                <ol className="text-xs text-amber-700 list-decimal list-inside space-y-1">
                  <li>Go to <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Google App Passwords</a></li>
                  <li>Select &quot;Mail&quot; and generate password</li>
                  <li>Copy the 16-character code and paste above</li>
                </ol>
                <p className="text-xs text-amber-600 mt-1">Note: Regular Gmail password will NOT work</p>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={handleSaveGmailSettings}
                  disabled={savingGmail || !gmailSettings.emailUser || !gmailSettings.emailPass}
                  className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 font-semibold flex items-center justify-center gap-2"
                >
                  {savingGmail ? (<><span className="animate-spin">⏳</span> Verifying...</>) : ('🔐 Save & Verify')}
                </button>
                <button
                  onClick={() => setShowGmailSettings(false)}
                  className="px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReservationDashboard;
